import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  Bot, Check, Copy, CornerUpLeft, Hand, Hash, Loader2, LogIn, MessagesSquare, MoreHorizontal, Palette, RefreshCw, Send,
  Star, User, X,
} from 'lucide-react';
import { waysApi, WaysError, type WaysChannel, type WaysMessage, type WaysSection, type WaysSidebarChannel, type WaysSidebarItem, type WaysSwitch } from '../services/waysApi';
import WaysGovernance from '../components/ways/WaysGovernance';
import WaysRunners from '../components/ways/WaysRunners';
import WaysActiveNow from '../components/ways/WaysActiveNow';
import WaysUsage from '../components/ways/WaysUsage';
import WaysClaudeEnrollment from '../components/ways/WaysClaudeEnrollment';
import { approvalsApi, type ApprovalRequest } from '../services/api';
import ApprovalPanel from '../components/approvals/ApprovalPanel';
import { hasSignedIn, signIn, silentTried, WaysSignInRequired } from '../services/waysAuth';
import { useAuthStore } from '../store/useAuthStore';
import WaysTechnology from '../components/ways/WaysTechnology';
import { useProjectStore } from '../store/useProjectStore';
import WaysSidebar from '../components/ways/WaysSidebar';
import WaysAsks from '../components/approvals/WaysAsks';
import WaysChannels from '../components/ways/WaysChannels';

/**
 * Ways in PMO (PO-92 F5.1): the person's sidebar -- "Needs you", Favorites, their own sections,
 * then General -- and a channel's message stream with each message's owner tag and work-item
 * chain. A held agent action shows as a card the person may allow or refuse. Ways decides what
 * is readable, postable and decidable; this page only shows it. Message text is rendered as
 * text, never as HTML.
 */

/**
 * The views the Ways navigation opens in the main area (PO46-1649). Channels, Runners and Usage
 * were right slideouts in 1.0.310; the channels one repeated the channel list already in the
 * navigation, which the design standard forbids (nav-slideouts, nav-two-paths). A right slideout
 * is only ever the detail of one item; a list or a destination is a view, here.
 */
const WAYS_VIEWS = ['respond', 'active', 'channels', 'runners', 'usage', 'technology'] as const;
type WaysView = typeof WAYS_VIEWS[number];
const isWaysView = (v: string | null): v is WaysView => !!v && (WAYS_VIEWS as readonly string[]).includes(v);
const VIEW_LABELS: Record<WaysView, string> = {
  respond: 'Needs my response', active: 'Active now', channels: 'Channels', runners: 'Runners', usage: 'Usage',
  technology: 'Technology',
};

const POLL_MS = 15_000;          // the open channel's messages
const SIDEBAR_POLL_MS = 60_000;  // the sidebar: rarely changes, and "Needs you" is not urgent to the second
const SIDE_DATA_POLL_MS = 180_000; // approvals and kill switches: off the sidebar poll (hs-pmo/ep43#35 b)
const PROJECT_RETRY_MS = 60_000;   // a project whose channels failed to load waits this long before a retry
const MAX_PROJECT_LOADS = 4;       // per-project channel loads in flight at once (ep43#35 c)
const SIDEBAR_CACHE_KEY = 'hs_ways_sidebar';
const WIDTH_KEY = 'ways.sidebarWidth';  // a per-device preference, so the browser keeps it
const DEFAULT_WIDTH = 288;
const MIN_WIDTH = 220;
const maxWidth = () => Math.round(window.innerWidth * 0.6);
const clampWidth = (w: number) => Math.max(MIN_WIDTH, Math.min(maxWidth(), Math.round(w)));

function savedWidth(): number {
  try {
    const w = Number(localStorage.getItem(WIDTH_KEY));
    return w ? clampWidth(w) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

/** The last sidebar this tab saw, shown at once while Ways answers. */
function cachedSidebar(): WaysSection[] | null {
  try {
    const raw = sessionStorage.getItem(SIDEBAR_CACHE_KEY);
    return raw ? (JSON.parse(raw) as WaysSection[]) : null;
  } catch {
    return null;
  }
}

function when(ts?: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleString();
}

function SignInCard({ onSignIn, message }: { onSignIn: () => void; message?: string }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-center">
        <MessagesSquare className="w-8 h-8 mx-auto mb-3 text-accent" />
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">Sign in to Ways</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          {message || 'Ways uses its own sign-in. With your Google session live this is one quick round trip.'}
        </p>
        <button type="button" onClick={onSignIn}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90">
          <LogIn className="w-4 h-4" /> Sign in
        </button>
      </div>
    </div>
  );
}

interface Hold {
  id: string;
  needs?: string;
  summary?: string;
  agent?: string | Record<string, unknown>;
}

function holdOf(m: WaysMessage): Hold | null {
  const h = (m.links || {})['hold'] as Hold | undefined;
  return h && typeof h === 'object' && h.id ? h : null;
}

/** A held agent action, asked of whoever holds the authority on the target project (D-7). */
function HoldCard({ hold, onDecide }: {
  hold: Hold;
  onDecide?: (decision: 'allow' | 'refuse', reason?: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const agent = typeof hold.agent === 'string' ? hold.agent : (hold.agent as { tag?: string } | undefined)?.tag;
  const decide = async (d: 'allow' | 'refuse') => {
    let reason: string | undefined;
    if (d === 'refuse') {
      reason = window.prompt('Why refuse? (the agent and its person see this)') || undefined;
      if (!reason?.trim()) return;
    }
    if (!onDecide) return;
    setBusy(true);
    try {
      await onDecide(d, reason);
      setDone(d === 'allow' ? 'Allowed: it runs once, under your authority.' : 'Refused.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 dark:text-amber-300">
        <Hand className="w-3.5 h-3.5" /> An agent's action is held for a decision
      </div>
      {hold.summary ? <p className="mt-1 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{hold.summary}</p> : null}
      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 flex flex-wrap gap-x-3">
        {hold.needs ? <span>needs <span className="font-mono">{hold.needs}</span></span> : null}
        {agent ? <span>agent <span className="font-mono">{agent}</span></span> : null}
      </div>
      {done ? (
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">{done}</p>
      ) : onDecide ? (
        <div className="mt-2 flex gap-2">
          <button type="button" disabled={busy} onClick={() => decide('allow')}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-50">
            <Check className="w-3.5 h-3.5" /> Allow
          </button>
          <button type="button" disabled={busy} onClick={() => decide('refuse')}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50">
            <X className="w-3.5 h-3.5" /> Refuse
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** One recipient as Ways sends it: a string, or {kind, display, id, initials, ...}. */
function recipientLabel(t: unknown): string {
  if (typeof t === 'string') return t;
  if (t && typeof t === 'object') {
    const o = t as Record<string, unknown>;
    for (const k of ['display', 'initials', 'role', 'id', 'kind']) {
      if (typeof o[k] === 'string' && o[k]) return o[k] as string;
    }
  }
  return '?';
}

export function MessageRow({ m, highlight, addTargets, onAddTo, onDecideHold, starred, onStar, onReply }: {
  m: WaysMessage;
  highlight?: boolean;
  /** In the person's Favorites; the star toggles it. */
  starred?: boolean;
  onStar?: () => void;
  /** Sections this message (as a conversation) can be added to. */
  addTargets?: { id: string; title: string }[];
  onAddTo?: (sectionId: string) => void;
  onDecideHold?: (holdId: string, decision: 'allow' | 'refuse', reason?: string) => Promise<void>;
  /** Reply to this message: inline (the arrow) or from its right-click menu. */
  onReply?: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!ctx) return;
    const close = () => setCtx(null);
    window.addEventListener('click', close);
    window.addEventListener('keydown', close);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', close); };
  }, [ctx]);
  const agent = m.author.actorClass === 'agent';
  const hold = holdOf(m);
  return (
    <li id={`ways-msg-${m.seq}`} data-testid="ways-message"
      onContextMenu={onReply ? (e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); } : undefined}
      className={clsx('group flex gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60',
        highlight && 'bg-accent/5 ring-1 ring-inset ring-accent/40')}>
      <div className={clsx('w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold',
        agent ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
          : 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300')}>
        {m.author.initials || (agent ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
          <span className="font-mono text-gray-600 dark:text-gray-300 truncate" title={m.author.sub || ''}>{m.author.text}</span>
          <span className="text-gray-400">{when(m.ts)}</span>
          {m.kind !== 'note' ? (
            <span className="px-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{m.kind}</span>
          ) : null}
          {m.needsReply ? (
            <span className="px-1.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">needs reply</span>
          ) : null}
          {m.to?.length ? <span className="text-gray-500">→ {(m.to as unknown[]).map(recipientLabel).join(', ')}</span> : null}
          {m.replyTo ? <span className="text-gray-400">re {m.replyTo}</span> : null}
          {onReply ? (
            <button type="button" onClick={onReply} title={`Reply to ${m.id}`} aria-label={`Reply to ${m.id}`}
              className="ml-auto p-0.5 rounded text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-accent">
              <CornerUpLeft className="w-3.5 h-3.5" />
            </button>
          ) : null}
          {onStar ? (
            <button type="button" onClick={onStar} title={starred ? 'Remove from Favorites' : 'Add to Favorites'}
              aria-label={starred ? 'Remove from Favorites' : 'Add to Favorites'}
              className={clsx(!onReply && 'ml-auto', 'p-0.5 rounded hover:text-amber-500',
                starred ? 'text-amber-500' : 'text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100')}>
              <Star className={clsx('w-3.5 h-3.5', starred && 'fill-current')} />
            </button>
          ) : null}
          {addTargets?.length && onAddTo ? (
            <span className={clsx('relative', !onStar && !onReply && 'ml-auto')}>
              <button type="button" title="Add to a section" aria-label="Add to a section"
                onClick={() => setMenu((v) => !v)}
                className="p-0.5 rounded text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-accent">
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
              {menu ? (
                <div className="absolute right-0 top-5 z-20 w-48 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-sm">
                  <div className="px-3 pt-1 pb-0.5 text-[11px] uppercase tracking-wide text-gray-400">Add to section</div>
                  {addTargets.map((s) => (
                    <button key={s.id} type="button" onClick={() => { setMenu(false); onAddTo(s.id); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700">
                      <Star className={clsx('w-3.5 h-3.5', s.id === 'favorites' ? 'text-amber-500' : 'text-gray-400')} />
                      <span className="truncate">{s.title}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          ) : null}
        </div>
        {m.chain?.length ? (
          <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
            {m.chain.map((c, i) => (
              <span key={`${c.level}-${c.id || i}`} className="inline-flex items-center gap-1">
                {i > 0 ? <span className="text-gray-300">›</span> : null}
                {c.id ? (
                  <Link to={c.level === 'po' ? `/project/${c.id}` : `/item/${c.id}`}
                    className="px-1.5 rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent hover:text-accent">
                    {c.display || c.id}
                  </Link>
                ) : (
                  <span className="px-1.5 rounded border border-gray-200 dark:border-gray-600 text-gray-500">{c.display}</span>
                )}
              </span>
            ))}
            {!m.workResolved ? <span className="text-gray-400 italic">unresolved</span> : null}
          </div>
        ) : null}
        <p className="mt-1 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">{m.text}</p>
        {hold ? (
          <HoldCard hold={hold}
            onDecide={onDecideHold ? (d, r) => onDecideHold(hold.id, d, r) : undefined} />
        ) : null}
      </div>
      {ctx && onReply ? (
        <div role="menu" style={{ position: 'fixed', left: ctx.x, top: ctx.y }}
          className="z-30 w-48 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-sm">
          <button type="button" role="menuitem" onClick={() => { setCtx(null); onReply(); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700">
            <CornerUpLeft className="w-3.5 h-3.5 text-gray-400" /> Reply
          </button>
          <button type="button" role="menuitem"
            onClick={() => { setCtx(null); void navigator.clipboard?.writeText(m.id).catch(() => undefined); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700">
            <Copy className="w-3.5 h-3.5 text-gray-400" /> Copy message id
          </button>
        </div>
      ) : null}
    </li>
  );
}

export default function WaysPage() {
  const { user } = useAuthStore();
  const projects = useProjectStore((s) => s.projects);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const [params, setParams] = useSearchParams();
  const active = params.get('channel');
  const focusSeq = Number(params.get('seq')) || null;

  const [needSignIn, setNeedSignIn] = useState<string | null>(hasSignedIn() ? null : '');
  const [sections, setSections] = useState<WaysSection[] | null>(cachedSidebar);
  const [collapsedNodes, setCollapsedNodes] = useState<string[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [projectChannels, setProjectChannels] = useState<Record<string, WaysSidebarChannel[] | 'loading' | 'denied'>>({});
  const [closedEpics, setClosedEpics] = useState<Record<string, { hidden: number; show: boolean }>>({});
  const [approvalsByProject, setApprovalsByProject] = useState<Record<string, ApprovalRequest[]>>({});
  const activeApproval = params.get('approval');
  const activeGovernance = params.get('governance');
  const runnerClaude = params.has('runner-claude');
  const rawView = params.get('view');
  const legacyPanel = params.get('panel');
  // Links from 1.0.310 (`?panel=channels|runners|usage`) open the same view in the main area; the
  // address is rewritten in place so Back does not bounce through the old one.
  useEffect(() => {
    if (legacyPanel === null) return;
    const next = new URLSearchParams(params);
    next.delete('panel');
    if (isWaysView(legacyPanel)) next.set('view', legacyPanel);
    setParams(next, { replace: true });
  }, [legacyPanel, params, setParams]);
  const requestedView: WaysView | null = isWaysView(rawView) ? rawView
    : isWaysView(legacyPanel) ? legacyPanel : null;
  const somethingOpen = !!(params.get('channel') || params.get('approval') || params.get('governance') || params.has('runner-claude'));
  // Nothing chosen: the main area is never an empty hint beside a list. It opens on what needs the
  // person (the channel list is already in the navigation on the left).
  const view: WaysView | null = requestedView ?? (somethingOpen ? null : 'respond');
  const openView = (v: WaysView) => setParams({ view: v });
  const [switches, setSwitches] = useState<WaysSwitch[]>([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const sidebarInFlight = useRef(false);
  const [messages, setMessages] = useState<WaysMessage[]>([]);
  const [channel, setChannel] = useState<WaysChannel | null>(null);
  const [loading, setLoading] = useState(false);
  const [older, setOlder] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  /** The message the composer replies to (inline arrow or right-click Reply); its id goes in replyTo. */
  const [replyTo, setReplyTo] = useState<WaysMessage | null>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  const startReply = (m: WaysMessage) => {
    setReplyTo(m);
    composer.current?.focus();
  };
  // A reply belongs to the channel it was started in: leaving the channel drops it, so it is
  // never sent into another channel with a replyTo pointing elsewhere.
  useEffect(() => { setReplyTo(null); }, [active]);
  const [sending, setSending] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  // The sidebar's width: drag its right edge, arrow keys on the focused edge, double-click resets.
  const [width, setWidthState] = useState<number>(savedWidth);
  const setWidth = useCallback((w: number) => {
    const c = clampWidth(w);
    setWidthState(c);
    try { localStorage.setItem(WIDTH_KEY, String(c)); } catch { /* storage off */ }
  }, []);
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const move = (ev: PointerEvent) => setWidth(startW + ev.clientX - startX);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Project names: the PMO project store, else the name Ways sends on its project rows, else the
  // id. The store is loaded once at app start; if that load failed (PMO briefly unreachable) or
  // this person's sidebar names a project it lacks, load it again -- once, not on every render --
  // so a channel tree never shows `project:1fa6...` where a name belongs.
  const waysProjectNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const sec of sections || []) {
      for (const it of sec.items || []) {
        if (it.type === 'project' && it.name) out[it.id] = it.name;
      }
    }
    return out;
  }, [sections]);
  const refetchedProjects = useRef(false);
  useEffect(() => {
    if (refetchedProjects.current) return;
    const known = new Set(projects.map((p) => p._id));
    const named = new Set(Object.keys(waysProjectNames));
    const missing = (sections || []).some((sec) => (sec.items || []).some((it) =>
      it.type === 'channel' && it.projectId && !known.has(it.projectId) && !named.has(it.projectId)));
    if (projects.length === 0 || missing) {
      refetchedProjects.current = true;
      void fetchProjects();
    }
  }, [projects, sections, waysProjectNames, fetchProjects]);
  // A map, not projects.find per row: the sidebar asks for every row on every render (ep43#35 g).
  const storeProjectNames = useMemo(
    () => Object.fromEntries(projects.map((p) => [p._id, p.name])) as Record<string, string>, [projects]);
  const projectName = useCallback(
    (id?: string | null) =>
      (id ? storeProjectNames[id] || waysProjectNames[id] : '') || id || '',
    [storeProjectNames, waysProjectNames],
  );

  const fail = useCallback((e: unknown) => {
    if (e instanceof WaysSignInRequired) setNeedSignIn(e.message);
    else setError(e instanceof Error ? e.message : String(e));
  }, []);

  // Windowed sections (ep43#35 e): General lists every project once asked, and Active keeps the
  // pages loaded with "Load more" across polls (the poll itself returns the first page only).
  const allProjects = useRef(false);
  const activeExtra = useRef<{ items: WaysSidebarItem[]; more: WaysSection['more'] }>({ items: [], more: undefined });
  const [loadingMore, setLoadingMore] = useState<string | null>(null);
  const withActiveExtra = useCallback((secs: WaysSection[]) => {
    const extra = activeExtra.current;
    if (!extra.items.length) return secs;
    return secs.map((s) => {
      if (s.id !== 'attention') return s;
      const seen = new Set(s.items.map((i) => `${i.type}:${i.id}`));
      return { ...s, items: [...s.items, ...extra.items.filter((i) => !seen.has(`${i.type}:${i.id}`))], more: extra.more };
    });
  }, []);

  const loadSidebar = useCallback(async () => {
    if (sidebarInFlight.current) return; // a slow answer must not stack up behind the poll
    sidebarInFlight.current = true;
    setSidebarLoading(true);
    try {
      const r = await waysApi.sidebar({ all: allProjects.current });
      setSections(withActiveExtra(r.sections));
      setCollapsedNodes(r.collapsed || []);
      setExpandedNodes(r.expanded || []);
      try { sessionStorage.setItem(SIDEBAR_CACHE_KEY, JSON.stringify(r.sections)); } catch { /* storage off */ }
      setError(null);
    } catch (e) {
      fail(e);
      setSections((s) => s ?? []);
    } finally {
      sidebarInFlight.current = false;
      setSidebarLoading(false);
    }
  }, [fail, withActiveExtra]);

  const loadMore = useCallback(async (sectionId: string) => {
    setLoadingMore(sectionId);
    try {
      if (sectionId === 'general') {
        allProjects.current = true;
        await loadSidebar();
        return;
      }
      const cur = (sections || []).find((s) => s.id === sectionId);
      const next = cur?.more?.nextOffset;
      if (sectionId !== 'attention' || next == null) return;
      const r = await waysApi.sidebar({ activeOffset: next, all: allProjects.current });
      const page = r.sections.find((s) => s.id === 'attention');
      activeExtra.current = { items: [...activeExtra.current.items, ...(page?.items || [])], more: page?.more ?? null };
      setSections((secs) => (secs ? withActiveExtra(secs) : secs));
    } catch (e) { fail(e); } finally { setLoadingMore(null); }
  }, [sections, loadSidebar, withActiveExtra, fail]);

  // Approvals this person may decide (for Active) and the kill switches: they change rarely and
  // cost PMO a read per request, so they have their own slow poll, not the sidebar's (ep43#35 b).
  const loadSideData = useCallback(() => {
    waysApi.killswitches().then((k) => setSwitches(k.switches || [])).catch(() => { /* older Ways: no switches */ });
    approvalsApi.pending().then((list) => {
      const by: Record<string, ApprovalRequest[]> = {};
      for (const a of list) if (a.projectId) (by[a.projectId] ||= []).push(a);
      setApprovalsByProject(by);
    }).catch(() => { /* Active still shows Ways' own counts */ });
  }, []);

  // The newest message shown, so the open channel's poll asks only for newer ones (ep43#35 f).
  const newestSeq = useRef(0);
  useEffect(() => { newestSeq.current = messages.reduce((n, m) => Math.max(n, m.seq || 0), 0); }, [messages]);

  // A quiet poll of the open channel: only what is newer, appended, acked when it moved.
  const pollNewer = useCallback(async (key: string) => {
    if (document.hidden || !newestSeq.current) return;
    try {
      const r = await waysApi.messages(key, { after: newestSeq.current, limit: 50 });
      if (!r.messages.length) return;
      setMessages((ms) => {
        const seen = new Set(ms.map((m) => m.seq));
        return [...ms, ...r.messages.filter((m) => !seen.has(m.seq))];
      });
      const newest = r.messages.reduce((n, m) => Math.max(n, m.seq || 0), 0);
      if (newest && document.visibilityState === 'visible') {
        await waysApi.ack({ [r.channel?.id || key]: newest }).catch(() => { /* next poll retries */ });
      }
    } catch (e) { fail(e); }
  }, [fail]);

  const loadMessages = useCallback(async (key: string, quiet = false, around: number | null = null) => {
    if (!quiet) setLoading(true);
    try {
      // A saved conversation opens with its message in the page: the 50 up to a little past it.
      const r = await waysApi.messages(key, around ? { before: around + 10, limit: 50 } : { limit: 50 });
      setChannel(r.channel);
      setMessages(r.messages);
      if (!quiet) setOlder(r.messages.length >= 50);
      setError(null);
      // Seen: Ways counts a message unread until it is acknowledged (reading consumes nothing),
      // so the newest one shown is acked while the page is visible (PO87-148).
      const newest = r.messages.reduce((n, m) => Math.max(n, m.seq || 0), 0);
      if (newest && document.visibilityState === 'visible') {
        await waysApi.ack({ [r.channel.id || key]: newest }).catch(() => { /* the badge stays; next load retries */ });
      }
    } catch (e) { fail(e); } finally { if (!quiet) setLoading(false); }
  }, [fail]);

  // Polls run only while the tab is visible; coming back refreshes at once (ep43#35 d).
  useEffect(() => {
    if (needSignIn !== null) return;
    loadSidebar();
    loadSideData();
    const t = setInterval(() => { if (!document.hidden) loadSidebar(); }, SIDEBAR_POLL_MS);
    const s = setInterval(() => { if (!document.hidden) loadSideData(); }, SIDE_DATA_POLL_MS);
    const back = () => { if (!document.hidden) { loadSidebar(); loadSideData(); } };
    document.addEventListener('visibilitychange', back);
    return () => { clearInterval(t); clearInterval(s); document.removeEventListener('visibilitychange', back); };
  }, [needSignIn, loadSidebar, loadSideData]);

  // Opening a channel loads it; the sidebar is NOT re-read for it (ep43#35 a): the channel's own
  // unread badge clears here, and the next sidebar poll reconciles the rest.
  useEffect(() => {
    if (needSignIn !== null || !active) return;
    loadMessages(active, false, focusSeq).then(() => setSections((secs) => secs && secs.map((sec) => ({
      ...sec,
      items: (sec.items || []).map((it) => (it.type === 'channel' && (it.id === active || it.key === active)
        ? { ...it, unread: 0 } : it)),
    }))));
    if (focusSeq) return; // reading history: do not poll it back to the latest page
    const t = setInterval(() => pollNewer(active), POLL_MS);
    const back = () => { if (!document.hidden) pollNewer(active); };
    document.addEventListener('visibilitychange', back);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', back); };
  }, [needSignIn, active, focusSeq, loadMessages, pollNewer]);

  useEffect(() => {
    if (loading) return;
    if (focusSeq) document.getElementById(`ways-msg-${focusSeq}`)?.scrollIntoView({ block: 'center' });
    else bottom.current?.scrollIntoView({ block: 'end' });
  }, [active, loading, focusSeq]);

  // A project's channels, read when its row is opened (and re-read with the sidebar when open).
  // Failed loads wait PROJECT_RETRY_MS before an automatic retry, and at most MAX_PROJECT_LOADS run
  // at once: a failing project used to be retried in a tight loop (ep43#35 c).
  const projectFailedAt = useRef<Record<string, number>>({});
  const projectLoads = useRef(0);
  const openProject = useCallback(async (projectId: string, auto = false) => {
    if (auto && Date.now() - (projectFailedAt.current[projectId] || 0) < PROJECT_RETRY_MS) return;
    if (auto && projectLoads.current >= MAX_PROJECT_LOADS) return;   // the next effect run takes it
    projectLoads.current += 1;
    setProjectChannels((m) => ({ ...m, [projectId]: m[projectId] && m[projectId] !== 'denied' ? m[projectId] : 'loading' }));
    try {
      const r = await waysApi.projectChannels(projectId);
      delete projectFailedAt.current[projectId];
      setProjectChannels((m) => ({ ...m, [projectId]: r.channels }));
      setClosedEpics((m) => ({ ...m, [projectId]: { hidden: r.closedEpics || 0, show: !!r.showClosed } }));
    } catch (e) {
      if (e instanceof WaysError && e.status === 404) setProjectChannels((m) => ({ ...m, [projectId]: 'denied' }));
      else {
        projectFailedAt.current[projectId] = Date.now();
        setProjectChannels((m) => { const n = { ...m }; delete n[projectId]; return n; });
        fail(e);
      }
    } finally {
      projectLoads.current -= 1;
    }
  }, [fail]);

  // Rows opened on another device load as soon as Ways says they are open.
  useEffect(() => {
    for (const pid of expandedNodes) if (!(pid in projectChannels)) openProject(pid, true);
  }, [expandedNodes, projectChannels, openProject]);

  const stoppedProjects = useMemo(
    () => new Set(switches.filter((s) => s.on && s.scope === 'project' && s.id).map((s) => s.id as string)),
    [switches]);

  const addTargets = useMemo(
    () => (sections || []).filter((s) => !s.builtin || s.id === 'favorites').map((s) => ({ id: s.id, title: s.title })),
    [sections],
  );

  const favoriteConversations = useMemo(() => new Set(
    ((sections || []).find((s) => s.id === 'favorites')?.items || [])
      .filter((i) => i.type === 'conversation').map((i) => i.id)), [sections]);

  const toggleStar = async (m: WaysMessage) => {
    try {
      if (favoriteConversations.has(m.id)) await waysApi.removeFromSection('favorites', 'conversation', m.id);
      else await waysApi.addToSection('favorites', 'conversation', m.id);
      await loadSidebar();
    } catch (e) { fail(e); }
  };

  const addConversation = async (m: WaysMessage, sectionId: string) => {
    try {
      await waysApi.addToSection(sectionId, 'conversation', m.id);
      await loadSidebar();
    } catch (e) { fail(e); }
  };

  const decideHold = async (holdId: string, decision: 'allow' | 'refuse', reason?: string) => {
    try {
      await waysApi.releaseHold(holdId, decision, reason);
      if (active) await loadMessages(active, true);
      await loadSidebar();
    } catch (e) {
      fail(e);
      throw e;
    }
  };

  const loadOlder = async () => {
    if (!active || !messages.length) return;
    try {
      const r = await waysApi.messages(active, { before: messages[0].seq, limit: 50 });
      setMessages((ms) => [...r.messages, ...ms]);
      setOlder(r.messages.length >= 50);
    } catch (e) { fail(e); }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || !active) return;
    setSending(true);
    try {
      await waysApi.send(replyTo
        ? { kind: 'reply', channel: active, replyTo: replyTo.id, text }
        : { kind: 'note', channel: active, text });
      setDraft('');
      setReplyTo(null);
      if (focusSeq) setParams({ channel: active });
      else await loadMessages(active, true);
      bottom.current?.scrollIntoView({ block: 'end' });
    } catch (e) { fail(e); } finally { setSending(false); }
  };

  // Not signed in to Ways yet: try the silent round trip once per tab before showing the button.
  useEffect(() => {
    if (needSignIn !== null && !silentTried()) signIn(user?.email, '/ways' + window.location.search, true);
  }, [needSignIn, user?.email]);

  if (needSignIn !== null) {
    if (!silentTried()) {
      return <div className="flex-1 flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
    }
    return <SignInCard message={needSignIn || undefined} onSignIn={() => signIn(user?.email, '/ways' + window.location.search)} />;
  }

  return (
    <div className="flex h-full min-h-0">
      <aside style={{ width }} className="relative shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <MessagesSquare className="w-4 h-4 text-accent" /> Ways
          </span>
          <button type="button" onClick={loadSidebar} title="Refresh" disabled={sidebarLoading}
            className="p-1 text-gray-400 hover:text-accent disabled:opacity-60">
            <RefreshCw className={clsx('w-3.5 h-3.5', sidebarLoading && 'animate-spin')} />
          </button>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 text-xs">
          {WAYS_VIEWS.map((v) => (
            <button key={v} type="button" onClick={() => openView(v)} aria-current={view === v ? 'page' : undefined}
              className={clsx('rounded hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                view === v ? 'text-accent font-medium' : 'text-gray-500 dark:text-gray-400')}>{VIEW_LABELS[v]}</button>
          ))}
          <Link to="/ways/design-standard" aria-label="Design standard" title="Design standard"
            className="ml-auto rounded text-gray-500 dark:text-gray-400 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            <Palette className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        </div>
        <WaysSidebar sections={sections} collapsedNodes={collapsedNodes} expandedNodes={expandedNodes}
          projectChannels={projectChannels} onOpenProject={openProject}
          closedEpics={closedEpics}
          onToggleClosedEpics={async (pid, show) => {
            try { await waysApi.setShowClosedEpics(pid, show); await openProject(pid); } catch (e) { fail(e); }
          }}
          approvalsByProject={approvalsByProject} activeApproval={activeApproval}
          onOpenApproval={(id) => setParams({ approval: id })}
          onOpenGovernance={(pid) => setParams({ governance: pid })}
          stoppedProjects={stoppedProjects}
          active={active} projectName={projectName}
          onOpenChannel={(key) => setParams({ channel: key })}
          onOpenConversation={(key, seq) => setParams({ channel: key, seq: String(seq) })}
          onChanged={loadSidebar} onError={fail} onLoadMore={loadMore} loadingMore={loadingMore} />
      </aside>
      <div role="separator" aria-orientation="vertical" aria-label="Resize the Ways sidebar"
        aria-valuemin={MIN_WIDTH} aria-valuenow={width} tabIndex={0}
        title="Drag to resize · double-click to reset"
        onPointerDown={startResize}
        onDoubleClick={() => setWidth(DEFAULT_WIDTH)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); setWidth(width - (e.shiftKey ? 64 : 16)); }
          if (e.key === 'ArrowRight') { e.preventDefault(); setWidth(width + (e.shiftKey ? 64 : 16)); }
          if (e.key === 'Home') { e.preventDefault(); setWidth(DEFAULT_WIDTH); }
        }}
        className="w-1.5 -ml-1 shrink-0 cursor-col-resize hover:bg-accent/40 focus:bg-accent/60 focus:outline-none z-10" />

      <section className="flex-1 min-w-0 flex flex-col bg-white dark:bg-gray-900">
        {error ? (
          <div className="px-4 py-2 text-sm text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 border-b border-red-200 dark:border-red-800">
            {error}
          </div>
        ) : null}
        {switches.some((s) => s.on) ? (
          <div className="px-4 py-2 text-sm text-red-800 bg-red-50 dark:bg-red-900/30 dark:text-red-200 border-b border-red-200 dark:border-red-800">
            {switches.filter((s) => s.on).map((s, i) => (
              <div key={i}>
                {s.actors === 'all' ? 'Everyone is' : 'Agents are'} stopped {s.scope === 'global' ? 'everywhere' : s.scope === 'project' ? `in ${projectName(s.id)}` : `in session ${s.id}`}: {s.reason || 'no reason given'}
                {s.scope === 'project' && s.id ? (
                  <button type="button" onClick={() => setParams({ governance: s.id as string })} className="ml-2 underline">Governance</button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {view === 'respond' ? (
          <WaysAsks />
        ) : view === 'active' ? (
          <WaysActiveNow projectName={projectName} />
        ) : view === 'channels' ? (
          <>
            <header className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <Hash className="w-4 h-4 text-accent" aria-hidden="true" /> Channels
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Every channel you can read. Choose one to open its messages here.</p>
            </header>
            <WaysChannels active={active} projectName={projectName} onError={fail}
              onOpen={(key) => setParams({ channel: key })} />
          </>
        ) : view === 'runners' ? (
          <WaysRunners projectName={projectName} />
        ) : view === 'usage' ? (
          <WaysUsage projectName={projectName} />
        ) : view === 'technology' ? (
          <WaysTechnology isSuperAdmin={user?.pmo_role === 'super_admin'} />
        ) : runnerClaude ? (
          <WaysClaudeEnrollment />
        ) : activeGovernance ? (
          <WaysGovernance projectId={activeGovernance} projectName={projectName(activeGovernance)}
            switches={switches} onSwitchesChanged={setSwitches} />
        ) : activeApproval ? (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Approval</h2>
              <Link to="/approvals" className="text-xs text-accent hover:underline">
                Open in Approvals
              </Link>
            </div>
            {/* PMO's own panel: the same decision path and rules as the Approvals page. */}
            <ApprovalPanel requestId={activeApproval} onChanged={() => { loadSidebar(); loadSideData(); }} />
          </div>
        ) : !active ? null : (
          <>
            <header className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{channel?.name || active}</div>
              <div className="text-xs text-gray-500 font-mono">{channel?.key || active}{channel?.projectId ? ` · ${projectName(channel.projectId)}` : ''}</div>
            </header>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : (
                <>
                  {older && messages.length ? (
                    <div className="text-center py-2">
                      <button type="button" onClick={loadOlder} className="text-xs text-accent hover:underline">Load older</button>
                    </div>
                  ) : null}
                  {messages.length ? (
                    <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                      {messages.map((m) => (
                        <MessageRow key={m.id} m={m} highlight={m.seq === focusSeq}
                          addTargets={addTargets} onAddTo={(sid) => addConversation(m, sid)}
                          starred={favoriteConversations.has(m.id)} onStar={() => toggleStar(m)}
                          onDecideHold={decideHold}
                          onReply={channel?.readOnly ? undefined : () => startReply(m)} />
                      ))}
                    </ul>
                  ) : (
                    <p className="px-4 py-10 text-center text-sm text-gray-500">No messages yet.</p>
                  )}
                  {focusSeq ? (
                    <div className="text-center py-2">
                      <button type="button" onClick={() => setParams({ channel: active })}
                        className="text-xs text-accent hover:underline">Jump to the latest</button>
                    </div>
                  ) : null}
                  <div ref={bottom} />
                </>
              )}
            </div>
            {channel?.readOnly ? (
              <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200 dark:border-gray-700">This channel is read-only.</div>
            ) : (
              <form className="flex flex-col gap-1.5 p-3 border-t border-gray-200 dark:border-gray-700"
                onSubmit={(e) => { e.preventDefault(); send(); }}>
                {replyTo ? (
                  <div data-testid="ways-replying" className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                    <CornerUpLeft className="w-3.5 h-3.5 text-accent shrink-0" />
                    <span className="shrink-0">Replying to <span className="font-mono">{replyTo.id}</span></span>
                    <span className="truncate text-gray-400">{replyTo.text.slice(0, 120)}</span>
                    <button type="button" onClick={() => setReplyTo(null)} title="Cancel the reply" aria-label="Cancel the reply"
                      className="ml-auto p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-100">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : null}
                <div className="flex gap-2">
                <textarea ref={composer} value={draft} onChange={(e) => setDraft(e.target.value)} rows={1}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                    else if (e.key === 'Escape' && replyTo) { e.preventDefault(); setReplyTo(null); }
                  }}
                  placeholder={replyTo ? `Reply to ${replyTo.id} (Enter to send, Esc to cancel)` : 'Write a note (Enter to send, Shift+Enter for a new line)'}
                  className="flex-1 resize-none px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent" />
                <button type="submit" disabled={sending || !draft.trim()} title="Send"
                  className="p-2 bg-accent text-white rounded-lg disabled:opacity-30">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
                </div>
              </form>
            )}
          </>
        )}
      </section>

    </div>
  );
}
